'use client';

// ═══════════════════════════════════════════════════════════════
// useNavigation Hook
// Theme-aware navigation utilities
// ═══════════════════════════════════════════════════════════════

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useThemeContext } from '../data-provider';
import { buildThemeUrl, ThemeViewType } from '../theme-router';

interface UseNavigationReturn {
    /** Current route */
    currentRoute: {
        view: string;
        slug?: string | null;
        query?: string | null;
    };

    /** Navigate to a view */
    navigateTo: (view: ThemeViewType, params?: { slug?: string; query?: string }) => void;

    /** Build a URL for a view */
    buildUrl: (view: ThemeViewType, params?: { slug?: string; query?: string }) => string;

    /** Navigate to product */
    goToProduct: (slug: string) => void;

    /** Navigate to collection */
    goToCollection: (slug?: string) => void;

    /** Navigate to cart */
    goToCart: () => void;

    /** Navigate to checkout */
    goToCheckout: () => void;

    /** Navigate to home */
    goHome: () => void;

    /** Go back */
    goBack: () => void;
}

/**
 * Navigation hook for theme-aware routing
 * Provides utility functions for common navigation actions
 */
export function useNavigation(): UseNavigationReturn {
    const { route } = useThemeContext();
    const router = useRouter();

    // Build URL helper
    const buildUrl = useCallback((
        view: ThemeViewType,
        params?: { slug?: string; query?: string }
    ): string => {
        return buildThemeUrl(view, params, 'ecommerce');
    }, []);

    // Navigate to view
    const navigateTo = useCallback((
        view: ThemeViewType,
        params?: { slug?: string; query?: string }
    ) => {
        const url = buildUrl(view, params);
        router.push(url);
    }, [router, buildUrl]);

    // Convenience navigators
    const goToProduct = useCallback((slug: string) => {
        navigateTo('PRODUCT', { slug });
    }, [navigateTo]);

    const goToCollection = useCallback((slug?: string) => {
        navigateTo('COLLECTION', slug ? { slug } : undefined);
    }, [navigateTo]);

    const goToCart = useCallback(() => {
        navigateTo('CART');
    }, [navigateTo]);

    const goToCheckout = useCallback(() => {
        navigateTo('CHECKOUT');
    }, [navigateTo]);

    const goHome = useCallback(() => {
        navigateTo('HOME');
    }, [navigateTo]);

    const goBack = useCallback(() => {
        router.back();
    }, [router]);

    return {
        currentRoute: route,
        navigateTo,
        buildUrl,
        goToProduct,
        goToCollection,
        goToCart,
        goToCheckout,
        goHome,
        goBack,
    };
}

export default useNavigation;
